import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useClient } from "../context/ClientContext";
import { PageHeader, Section, Card, CardHeader, Pill, LoadingBar } from "../components/Primitives";
import {
  UploadSimple, Link, NotePencil, FileText, Sparkle, CircleNotch,
  CheckCircle, Warning, ArrowRight, Plus, FilePdf, X, Tag
} from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";

const KIND_ICON = {
  requirements: FileText, test_cases: NotePencil, diagram: FilePdf, link: Link, note: NotePencil,
};

const CRIT_TONE = { high: "danger", medium: "warn", low: "neutral" };
const PRIO_TONE = { P0: "danger", P1: "warn", P2: "neutral" };

function fmtAgo(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function IntakeHub() {
  const { activeClient, activeClientId } = useClient();
  const [areas, setAreas] = useState([]);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openUpload, setOpenUpload] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [recommendation, setRecommendation] = useState(null);
  const [analysing, setAnalysing] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [error, setError] = useState("");

  async function refresh() {
    if (!activeClientId) return;
    setLoading(true);
    const [a, d] = await Promise.all([
      api.get("/business-areas", { params: { client_id: activeClientId } }),
      api.get("/documents", { params: { client_id: activeClientId } }),
    ]);
    setAreas(a.data); setDocs(d.data); setLoading(false);
  }

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [activeClientId]);

  async function openDoc(doc) {
    setSelectedDoc(doc); setRecommendation(null);
    const r = await api.get(`/documents/${doc.id}/recommendations`);
    if (r.data.length) setRecommendation(r.data[0]);
  }

  async function analyse() {
    if (!selectedDoc) return;
    setAnalysing(true); setError("");
    try {
      const r = await api.post(`/documents/${selectedDoc.id}/analyse`);
      setRecommendation(r.data);
      refresh();
    } catch (e) {
      setError(e?.response?.data?.detail || "Analysis failed");
    } finally { setAnalysing(false); }
  }

  async function promote() {
    if (!recommendation) return;
    setPromoting(true);
    try {
      await api.post(`/recommendations/${recommendation.id}/promote-scenarios`);
      alert("Scenarios added to the library.");
    } finally { setPromoting(false); }
  }

  if (loading) return <LoadingBar label="Loading intake hub" />;

  return (
    <div data-testid="intake-hub-page">
      <PageHeader
        overline={`Layer 0 · ${activeClient?.name || ""} / Knowledge Intake`}
        title="Intake & Intelligence"
        description="Ingest requirements, test cases, diagrams, SharePoint links and unstructured docs. The AI maps each requirement to workflow steps or flags it as a non-workflow concern."
        right={
          <button
            data-testid="open-upload-btn"
            onClick={() => setOpenUpload(true)}
            className="inline-flex items-center gap-2 bg-vlink-500 hover:bg-vlink-600 text-white text-sm font-medium px-4 py-2.5 rounded-sm transition-colors"
          >
            <Plus size={14} /> Add Document
          </button>
        }
      />

      <Section>
        {/* Business areas */}
        <div className="mb-8">
          <div className="overline text-zinc-500 mb-3">Business-critical areas</div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="business-areas-grid">
            {areas.map((a) => (
              <Card key={a.id} className="p-4" testid={`area-${a.key}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="font-medium text-sm text-zinc-900">{a.name}</div>
                  <Pill tone={CRIT_TONE[a.criticality]}>{a.criticality}</Pill>
                </div>
                <div className="text-[11px] text-zinc-500 leading-snug">{a.description}</div>
                <div className="mt-3 flex items-center gap-2 text-[11px] font-mono text-zinc-600">
                  <Tag size={11} />
                  <span>{a.doc_count} doc{a.doc_count !== 1 ? "s" : ""}</span>
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Documents */}
        <Card testid="documents-card">
          <CardHeader
            overline={`Inventory · ${docs.length} documents`}
            title="Ingested Knowledge"
            right={<Pill tone="ai"><Sparkle size={11} className="mr-1" /> Claude 4.5 analyses</Pill>}
          />
          {docs.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <FileText size={32} className="mx-auto text-vlink-500" />
              <div className="mt-4 font-heading text-lg text-zinc-900">No documents yet</div>
              <p className="mt-2 text-sm text-zinc-600 max-w-md mx-auto">
                Add requirements docs, test cases, integration diagrams, SharePoint links or any unstructured notes.
                The AI will map each requirement to a workflow step or flag it as non-workflow.
              </p>
              <button
                onClick={() => setOpenUpload(true)}
                className="mt-5 inline-flex items-center gap-2 bg-vlink-500 hover:bg-vlink-600 text-white text-sm font-medium px-4 py-2.5 rounded-sm"
              >
                <Plus size={14} /> Add your first document
              </button>
            </div>
          ) : (
            <div className="divide-y divide-zinc-100">
              {docs.map((d) => {
                const Icon = KIND_ICON[d.kind] || FileText;
                return (
                  <button
                    key={d.id}
                    data-testid={`doc-${d.id}`}
                    onClick={() => openDoc(d)}
                    className="w-full text-left px-6 py-4 flex items-center gap-4 hover:bg-zinc-50 transition-colors"
                  >
                    <div className="w-9 h-9 border border-zinc-200 bg-zinc-50 flex items-center justify-center flex-shrink-0">
                      <Icon size={16} className="text-zinc-700" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-zinc-900 truncate">{d.title}</div>
                      <div className="text-[11px] text-zinc-500 font-mono mt-0.5 flex items-center gap-2 flex-wrap">
                        <span>{d.kind}</span>
                        <span>·</span>
                        <span>{d.source_type}</span>
                        {d.filename && <><span>·</span><span>{d.filename}</span></>}
                        <span>·</span>
                        <span>{fmtAgo(d.uploaded_at)}</span>
                      </div>
                      {d.business_area_keys?.length > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          {d.business_area_keys.slice(0, 5).map((k) => (
                            <span key={k} className="text-[10px] px-1.5 py-0.5 bg-vlink-50 text-vlink-700 border border-vlink-200">{k}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {d.status === "analysed" ? (
                        <Pill tone="success"><CheckCircle size={11} className="mr-1" /> analysed</Pill>
                      ) : (
                        <Pill tone="warn"><Warning size={11} className="mr-1" /> ingested</Pill>
                      )}
                      <ArrowRight size={14} className="text-zinc-400" />
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </Card>
      </Section>

      {/* Upload modal */}
      <AnimatePresence>
        {openUpload && (
          <UploadModal
            client={activeClient}
            areas={areas}
            onClose={() => setOpenUpload(false)}
            onSaved={() => { setOpenUpload(false); refresh(); }}
          />
        )}
      </AnimatePresence>

      {/* Document drawer */}
      <AnimatePresence>
        {selectedDoc && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 z-30"
            onClick={() => setSelectedDoc(null)}
          >
            <motion.aside
              initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.22 }}
              onClick={(e) => e.stopPropagation()}
              className="absolute right-0 top-0 bottom-0 w-full max-w-2xl bg-white border-l border-zinc-200 flex flex-col"
              data-testid="doc-drawer"
            >
              <div className="px-8 py-6 border-b border-zinc-200 flex items-start justify-between gap-4">
                <div>
                  <div className="overline text-zinc-500">Document</div>
                  <div className="font-heading text-2xl text-zinc-900 mt-1">{selectedDoc.title}</div>
                  <div className="text-xs text-zinc-500 font-mono mt-1">{selectedDoc.kind} · {selectedDoc.source_type} · {fmtAgo(selectedDoc.uploaded_at)}</div>
                </div>
                <button onClick={() => setSelectedDoc(null)} data-testid="close-doc-drawer" className="text-zinc-400 hover:text-zinc-900">
                  <X size={20} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-8 py-6">
                {!recommendation ? (
                  <div className="ai-tracing-beam p-6 bg-vlink-50/30">
                    <Sparkle size={20} className="text-vlink-600" />
                    <div className="mt-3 font-heading text-lg text-zinc-900">Ready to analyse</div>
                    <p className="mt-2 text-sm text-zinc-600 leading-relaxed">
                      Claude will read this document, extract requirements, map workflow-based ones to specific workflow steps,
                      flag non-workflow scenarios (config, perf, security, data-quality), and recommend a test strategy.
                    </p>
                    {error && <div className="mt-3 text-xs text-rose-700 bg-rose-50 border border-rose-200 px-3 py-2 rounded-sm">{error}</div>}
                    <button
                      data-testid="analyse-btn"
                      onClick={analyse}
                      disabled={analysing}
                      className="mt-4 inline-flex items-center gap-2 bg-vlink-500 hover:bg-vlink-600 text-white text-sm font-medium px-4 py-2.5 rounded-sm disabled:opacity-60"
                    >
                      {analysing ? <CircleNotch size={14} className="animate-spin" /> : <Sparkle size={14} />}
                      {analysing ? "Analysing with Claude…" : "Analyse with AI"}
                    </button>
                  </div>
                ) : (
                  <RecommendationView rec={recommendation} onPromote={promote} promoting={promoting} />
                )}
              </div>
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function RecommendationView({ rec, onPromote, promoting }) {
  return (
    <div className="space-y-6" data-testid="recommendation-view">
      <div className="border border-vlink-200 bg-vlink-50/40 px-5 py-4">
        <div className="overline text-vlink-700 mb-1 flex items-center gap-2"><Sparkle size={11} /> AI Summary</div>
        <p className="text-sm text-zinc-900 leading-relaxed">{rec.summary}</p>
        <p className="mt-3 text-xs text-zinc-700 leading-relaxed border-t border-vlink-200 pt-3">
          <span className="font-medium">Strategy: </span>{rec.recommendation}
        </p>
      </div>

      {rec.business_area_impact?.length > 0 && (
        <div>
          <div className="overline text-zinc-500 mb-3">Business-area impact</div>
          <div className="grid grid-cols-2 gap-2">
            {rec.business_area_impact.map((b, i) => (
              <div key={i} className="border border-zinc-200 px-3 py-2 text-xs">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-mono text-zinc-700">{b.key}</span>
                  <Pill tone={CRIT_TONE[b.impact]}>{b.impact}</Pill>
                </div>
                <div className="text-[11px] text-zinc-600">{b.note}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="overline text-zinc-500 mb-3">Requirements extracted ({rec.requirements?.length || 0})</div>
        <ul className="space-y-2">
          {rec.requirements?.map((r, i) => (
            <li key={i} className="border border-zinc-200 px-3 py-2.5 text-sm">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <span className="font-mono text-[11px] text-zinc-400">{r.id}</span>
                  <span className="ml-2 text-zinc-900">{r.text}</span>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Pill tone="neutral">{r.business_area}</Pill>
                  <Pill tone={CRIT_TONE[r.criticality]}>{r.criticality}</Pill>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="overline text-zinc-500">Workflow-based scenarios</div>
            <div className="text-[11px] text-zinc-500 mt-0.5">End-to-end, mapped to workflow steps</div>
          </div>
          <button
            data-testid="promote-scenarios-btn"
            onClick={onPromote}
            disabled={promoting || !(rec.workflow_scenarios?.length)}
            className="text-xs border border-vlink-500 text-vlink-700 hover:bg-vlink-50 px-3 py-1.5 rounded-sm disabled:opacity-60"
          >
            {promoting ? "Adding…" : `Promote to library (${rec.workflow_scenarios?.length || 0})`}
          </button>
        </div>
        <ul className="space-y-2">
          {rec.workflow_scenarios?.map((s, i) => (
            <li key={i} className="border-l-2 border-l-vlink-500 border border-zinc-200 px-3 py-2.5 text-sm">
              <div className="flex items-start justify-between gap-2 mb-1">
                <div className="text-zinc-900">{s.title}</div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Pill tone={PRIO_TONE[s.priority]}>{s.priority}</Pill>
                  <Pill tone="neutral">{s.type}</Pill>
                </div>
              </div>
              <div className="text-[11px] text-zinc-600 italic mb-1">{s.rationale}</div>
              <div className="text-[10px] font-mono text-zinc-500">
                {s.workflow_id} {s.workflow_node_id ? `→ ${s.workflow_node_id}` : "(node unmapped)"}
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <div className="overline text-zinc-500 mb-3">Non-workflow scenarios</div>
        <p className="text-[11px] text-zinc-500 mb-2">Single-component or cross-cutting concerns (config, perf, security, data-quality)</p>
        <ul className="space-y-2">
          {rec.non_workflow_scenarios?.map((s, i) => (
            <li key={i} className="border-l-2 border-l-amber-500 border border-zinc-200 px-3 py-2.5 text-sm">
              <div className="flex items-start justify-between gap-2 mb-1">
                <div className="text-zinc-900">{s.title}</div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Pill tone={PRIO_TONE[s.priority]}>{s.priority}</Pill>
                  <Pill tone="warn">{s.category}</Pill>
                </div>
              </div>
              <div className="text-[11px] text-zinc-600 italic">{s.rationale}</div>
            </li>
          ))}
        </ul>
      </div>

      {rec.risks?.length > 0 && (
        <div>
          <div className="overline text-zinc-500 mb-2">Risks flagged</div>
          <ul className="space-y-1">
            {rec.risks.map((r, i) => (
              <li key={i} className="flex gap-2 text-xs text-rose-800 bg-rose-50 border border-rose-200 px-3 py-2 rounded-sm">
                <Warning size={13} className="flex-shrink-0 mt-0.5" />
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function UploadModal({ client, areas, onClose, onSaved }) {
  const [mode, setMode] = useState("paste"); // paste | link | upload
  const [title, setTitle] = useState("");
  const [kind, setKind] = useState("requirements");
  const [content, setContent] = useState("");
  const [link, setLink] = useState("");
  const [file, setFile] = useState(null);
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  function toggleArea(key) {
    setPicked((p) => p.includes(key) ? p.filter((k) => k !== key) : [...p, key]);
  }

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      if (mode === "upload") {
        if (!file) throw new Error("Please pick a file");
        const form = new FormData();
        form.append("client_id", client.id);
        form.append("title", title || file.name);
        form.append("kind", kind);
        form.append("business_areas_csv", picked.join(","));
        form.append("file", file);
        await api.post("/documents/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      } else {
        await api.post("/documents", {
          client_id: client.id,
          title: title || (mode === "link" ? link : "Untitled note"),
          kind: mode === "link" ? "link" : kind,
          source_type: mode,
          source_url: mode === "link" ? link : null,
          content_text: mode === "paste" ? content : (mode === "link" ? `Linked resource: ${link}\nTitle: ${title}` : ""),
          business_area_keys: picked,
        });
      }
      onSaved();
    } catch (e2) {
      setError(e2?.response?.data?.detail || e2.message || "Failed");
    } finally { setBusy(false); }
  }

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-40 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 16, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 8, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-zinc-200"
        data-testid="upload-modal"
      >
        <div className="px-7 py-5 border-b border-zinc-200 flex items-start justify-between">
          <div>
            <div className="overline text-zinc-500">Intake</div>
            <div className="font-heading text-2xl text-zinc-900 mt-1">Add knowledge</div>
            <div className="text-xs text-zinc-500 mt-1">for {client?.name} / {client?.platform}</div>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-900" data-testid="close-upload">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={submit} className="px-7 py-5 space-y-5">
          {/* Mode tabs */}
          <div className="flex gap-1 border border-zinc-200 p-1 bg-zinc-50 w-fit">
            {[
              { v: "paste", label: "Paste text", icon: NotePencil },
              { v: "link", label: "Link", icon: Link },
              { v: "upload", label: "Upload file", icon: UploadSimple },
            ].map(({ v, label, icon: Ic }) => (
              <button
                type="button"
                key={v}
                data-testid={`upload-mode-${v}`}
                onClick={() => setMode(v)}
                className={`px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1.5 rounded-sm transition-colors ${
                  mode === v ? "bg-white text-zinc-900 border border-zinc-300" : "text-zinc-600 hover:text-zinc-900"
                }`}
              >
                <Ic size={12} /> {label}
              </button>
            ))}
          </div>

          <div>
            <label className="overline text-zinc-500 mb-1.5 block">Title</label>
            <input
              data-testid="upload-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Q1 2026 Settlement Workflow Requirements"
              className="w-full border border-zinc-300 focus:border-vlink-500 outline-none px-3 py-2.5 text-sm rounded-sm"
            />
          </div>

          <div>
            <label className="overline text-zinc-500 mb-1.5 block">Type</label>
            <select
              data-testid="upload-kind"
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              className="w-full border border-zinc-300 focus:border-vlink-500 outline-none px-3 py-2.5 text-sm rounded-sm bg-white"
            >
              <option value="requirements">Requirements / spec</option>
              <option value="test_cases">Existing test cases</option>
              <option value="diagram">Integration diagram / architecture</option>
              <option value="note">Note / minutes / context</option>
            </select>
          </div>

          {mode === "paste" && (
            <div>
              <label className="overline text-zinc-500 mb-1.5 block">Content</label>
              <textarea
                data-testid="upload-content"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={8}
                placeholder="Paste requirements, test cases, meeting notes, system spec, etc."
                className="w-full border border-zinc-300 focus:border-vlink-500 outline-none px-3 py-2.5 text-sm font-mono rounded-sm"
              />
            </div>
          )}

          {mode === "link" && (
            <div>
              <label className="overline text-zinc-500 mb-1.5 block">URL (SharePoint, Confluence, etc.)</label>
              <input
                data-testid="upload-link"
                value={link}
                onChange={(e) => setLink(e.target.value)}
                placeholder="https://sharepoint.company.com/sites/qa/requirements.docx"
                className="w-full border border-zinc-300 focus:border-vlink-500 outline-none px-3 py-2.5 text-sm font-mono rounded-sm"
              />
              <div className="text-[11px] text-zinc-500 mt-1">Stored as reference. For AI analysis, also paste key text in the Content section after creating.</div>
            </div>
          )}

          {mode === "upload" && (
            <div>
              <label className="overline text-zinc-500 mb-1.5 block">File (PDF, DOCX, TXT, MD, CSV)</label>
              <input
                data-testid="upload-file"
                type="file"
                accept=".pdf,.docx,.txt,.md,.csv"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="w-full text-sm border border-zinc-300 px-3 py-2 bg-white rounded-sm"
              />
              {file && (
                <div className="mt-2 text-xs text-zinc-600 font-mono">
                  {file.name} · {(file.size / 1024).toFixed(1)} KB
                </div>
              )}
            </div>
          )}

          <div>
            <label className="overline text-zinc-500 mb-2 block">Business-critical areas</label>
            <div className="flex flex-wrap gap-1.5">
              {areas.map((a) => (
                <button
                  type="button"
                  key={a.key}
                  data-testid={`area-toggle-${a.key}`}
                  onClick={() => toggleArea(a.key)}
                  className={`text-xs px-2.5 py-1 border rounded-sm transition-colors ${
                    picked.includes(a.key)
                      ? "bg-vlink-500 text-white border-vlink-500"
                      : "bg-white text-zinc-700 border-zinc-300 hover:border-vlink-500"
                  }`}
                >
                  {a.name}
                </button>
              ))}
            </div>
          </div>

          {error && <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 px-3 py-2 rounded-sm">{error}</div>}

          <div className="flex justify-end gap-2 pt-3 border-t border-zinc-100">
            <button type="button" onClick={onClose} className="text-sm text-zinc-600 hover:text-zinc-900 px-4 py-2">
              Cancel
            </button>
            <button
              type="submit"
              data-testid="upload-submit"
              disabled={busy}
              className="bg-vlink-500 hover:bg-vlink-600 text-white text-sm font-medium px-5 py-2.5 rounded-sm inline-flex items-center gap-2 disabled:opacity-60"
            >
              {busy ? <CircleNotch size={13} className="animate-spin" /> : <Plus size={13} />}
              {busy ? "Saving…" : "Save document"}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}
